import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import HomePage from "./pages/HomePage";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import AnalyticsTracker from "./components/AnalyticsTracker";
import { useLanguage } from "./context/LanguageContext";
import { trackVisit } from "./utils/trackVisit";
import { useAuth, AuthProvider } from "./context/AuthContext";
import LoadingPage from "./components/LoadingPage";
import UploadStatusIndicator from "./components/UploadStatusIndicator";
import { sectionPath, sectionRoutePath, legacyEnRoutePath } from "./utils/routePaths";

// Every page except HomePage (the most common landing page, kept in the main
// bundle to avoid an extra network round trip on first paint) is loaded on
// demand. Previously every page — including the entire admin panel with its
// rich text editors, map editors, and dozens of admin forms — shipped in one
// ~2.4MB bundle to every visitor, regardless of which single page they
// actually opened.
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const PlaceDetailsPage = lazy(() => import("./pages/PlaceDetailsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const AdventuresPage = lazy(() => import("./pages/AdventuresPage"));
const AdventureRoutePage = lazy(() => import("./pages/AdventureRoutePage"));
const CulturePage = lazy(() => import("./pages/CulturePage"));
const CultureArticlePage = lazy(() => import("./pages/CultureArticlePage"));
const TravelGuidePage = lazy(() => import("./pages/TravelGuidePageUpdate"));
const PhotoGuidePage = lazy(() => import("./pages/PhotoGuidePage"));
const CuisinePage = lazy(() => import("./pages/CuisinePage"));
const FestivalsPage = lazy(() => import("./pages/FestivalsPage"));
const LocalGuidesPage = lazy(() => import("./pages/LocalGuidesPage"));
const LocalGuideDetailPage = lazy(() => import("./pages/LocalGuideDetailPage"));
const HotelDetailPage = lazy(() => import("./pages/HotelDetailPage"));
const HotelsPage = lazy(() => import("./pages/HotelsPage"));

// Every non-home, language-prefixed route. `keys` maps to src/utils/routePaths.js
// SECTIONS/SUBSECTIONS so the URL segment is localized (English word under
// /en/, Georgian word under /ka/). Each entry renders three <Route>s: the
// English-canonical path, the Georgian-canonical (localized) path, and a
// /ka/<english-segment>/... legacy redirect so old shared/indexed links keep
// working after the segment is translated.
const ROUTE_DEFS = [
  { keys: "categories", element: <CategoryPage /> },
  { keys: "place", param: "slug", element: <PlaceDetailsPage /> },
  { keys: "about", element: <AboutPage /> },
  { keys: "adventures", element: <AdventuresPage /> },
  { keys: "adventures", param: "slug", element: <AdventuresPage /> },
  { keys: ["adventures", "route"], param: "slug", element: <AdventureRoutePage /> },
  { keys: "culture", element: <CulturePage /> },
  { keys: "culture", param: "slug", element: <CulturePage /> },
  { keys: ["culture", "article"], param: "slug", element: <CultureArticlePage /> },
  { keys: "guide", element: <TravelGuidePage /> },
  { keys: "guide", param: "slug", element: <TravelGuidePage /> },
  { keys: "photo-guide", element: <PhotoGuidePage /> },
  { keys: "photo-guide", param: "slug", element: <PhotoGuidePage /> },
  { keys: "cuisine", element: <CuisinePage /> },
  { keys: "cuisine", param: "slug", element: <CuisinePage /> },
  { keys: "festivals", element: <FestivalsPage /> },
  { keys: "festivals", param: "slug", element: <FestivalsPage /> },
  { keys: "local-guides", element: <LocalGuidesPage /> },
  { keys: "local-guides", param: "slug", element: <LocalGuideDetailPage /> },
  { keys: "hotels", element: <HotelsPage /> },
  { keys: "hotels", param: "slug", element: <HotelDetailPage /> },
];

function LegacyKaRedirect({ keys }) {
  const params = useParams();
  const rest = Object.values(params).filter((v) => v !== undefined);
  return <Navigate to={sectionPath("ka", keys, ...rest)} replace />;
}

function AppRoutes() {
  const { language } = useLanguage();
  useEffect(() => {
    trackVisit();
  }, []);
  return (
  <Suspense fallback={<LoadingPage />}>
  <Routes>
    {/* Admin and login — MUST come first, before /:lang routes, to avoid path collisions.
        AuthProvider is scoped to just these three routes (rather than wrapping the whole
        app in main.jsx) so Firebase Auth — and the auth iframe it loads from
        {authDomain}/__/auth/iframe.js — never loads on public pages that never touch it. */}
    <Route path="/login" element={<AuthProvider><LoginPage /></AuthProvider>} />
    <Route path="/admin" element={<AuthProvider><ProtectedRoute><AdminPage /></ProtectedRoute></AuthProvider>} />
    <Route path="/admin/:section" element={<AuthProvider><ProtectedRoute><AdminPage /></ProtectedRoute></AuthProvider>} />

    {/* Redirect root to language prefix */}
    <Route path="/" element={<Navigate to={`/${language}`} replace />} />

    {/* Language-prefixed routes */}
    <Route path="/:lang" element={<HomePage />} />

    {/* Canonical English-segment routes */}
    {ROUTE_DEFS.map(({ keys, param, element }, i) => (
      <Route key={`en-${i}`} path={sectionRoutePath("en", keys, param)} element={element} />
    ))}

    {/* Canonical Georgian-segment routes */}
    {ROUTE_DEFS.map(({ keys, param, element }, i) => (
      <Route key={`ka-${i}`} path={sectionRoutePath("ka", keys, param)} element={element} />
    ))}

    {/* Legacy /ka/<english-segment>/... links redirect to the localized Georgian path */}
    {ROUTE_DEFS.map(({ keys, param }, i) => (
      <Route
        key={`ka-legacy-${i}`}
        path={legacyEnRoutePath("ka", keys, param)}
        element={<LegacyKaRedirect keys={keys} />}
      />
    ))}
  </Routes>
  </Suspense>
);
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AnalyticsTracker />
      <AppRoutes />
      <UploadStatusIndicator />
    </BrowserRouter>
  );
}
